/**
 * This file contains declaration of smaller services,
 * commonly used as building blocks to create more specific
 * endpoint wrapping services
 */
import type { ErrorInfo } from 'react';

import { type CreateUIAnalyticsEvent, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { AbstractServiceMarker } from '../../core/types';
import { unimplementedAbstractService } from '../../core/utils';

// A service with a default implementation (an override can still be provided, optionally):
export const getStargateURL = (url: `/${string}`) => `/gateway/api${url}`;

// An abstract service that should always be provided:
export const getCurrentCloudId: (() => string) & AbstractServiceMarker =
	unimplementedAbstractService<() => string>('getCurrentCloudId');

export const getCurrentOrgId: (() => string) & AbstractServiceMarker =
	unimplementedAbstractService<() => string>('getCurrentOrgId');

export const getCurrentUserId: (() => string) & AbstractServiceMarker =
	unimplementedAbstractService<() => string>('getCurrentUserId');
export type Product =
	| 'confluence'
	| 'jira'
	| 'jira-software'
	| 'jira-servicedesk'
	| 'jira-core'
	| 'jira-product-discovery'
	| 'loom'
	| 'trello'
	| 'bitbucket'
	| 'statuspage'
	| 'opsgenie'
	| 'townsquare' // Atlas
	| 'compass'
	| 'avp' // Atlassian analytics
	| 'mercury' // Focus
	| 'cmdb'
	| 'jira-incident-manager'
	| 'jira-customer-service'
	| 'hipchat.cloud'
	| 'avocado'
	| 'beacon'
	| 'canvas'
	| 'unified-help'
	| 'access'
	| 'atlassian-together'
	| 'rovo'
	| 'goal'
	| 'project'
	| 'devai'
	| 'radar'
	| 'jira-align'
	| 'passionfruit';
export type UserAccessLevel = 'EXTERNAL' | 'INTERNAL' | 'INTERNAL_INACTIVE';

export const getCurrentProduct: (() => Product) & AbstractServiceMarker =
	unimplementedAbstractService<() => Product>('getCurrentProduct');
export const createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker =
	unimplementedAbstractService<CreateUIAnalyticsEvent>('createAnalyticsEvent');
export type AnalyticsMeta = {
	id: string;
	packageName?: string;
	teamName?: string;
};
export type AnalyticsAttributes = {
	[key: string]:
		| boolean
		| string
		| number
		| AnalyticsAttributes
		| AnalyticsAttributes[]
		| undefined
		| null;
};
export type AnalyticsPayload = {
	attributes?: AnalyticsAttributes;
	error?: Error;
	errorInfo?: ErrorInfo;
	event?: UIAnalyticsEvent;
	meta: AnalyticsMeta;
	sendToPrivacyUnsafeSplunk?: boolean;
	skipSentry?: boolean;
};
export const fireErrorAnalytics: ((input: AnalyticsPayload) => unknown) & AbstractServiceMarker =
	unimplementedAbstractService<AnalyticsPayload>('fireErrorAnalytics');

export class FetchError extends Error {
	responseCode: number;
	errorType?: string;
	response: Response;
	responseJson?: any;

	constructor(
		action: string,
		responseCode: number,
		response: Response,
		responseJson: any | undefined = undefined,
		errorType: string | undefined = undefined,
	) {
		if (responseJson?.message) {
			super(responseJson.message);
		} else {
			super(`Received error response during action ${action}: ${response.statusText}`);
		}
		this.name = 'FetchError';
		this.responseCode = responseCode;
		this.response = response;
		this.responseJson = responseJson;
		this.errorType = errorType;
	}
}

export const getErrorDetails = (
	err: unknown,
): {
	code: string;
	message: string;
	status: number | undefined;
} => ({
	code: err instanceof Error ? err.name : 'UNKNOWN',
	message: err instanceof Error ? err.message : String(err),
	status: getErrorStatus(err),
});

// Will try to get the status code (404, 500 etc.) from multiple error properties
// because Expansion Services can be used in different environments,
// which may dependency-inject different fetchJSON() implementations.
// These throw different FetchError shapes across the main products.
// Because we're dealing with exceptions, this is not guarded by types.
type ProductErrorWithStatus = {
	response?: Partial<Pick<Response, 'status'>>;
	responseCode?: number;
	status?: number;
	statusCode?: number;
};
const getErrorStatus = (error: unknown): number | undefined => {
	if (!error) {
		return undefined;
	}
	const e = error as ProductErrorWithStatus;
	return e.statusCode ?? e.status ?? e.responseCode ?? e.response?.status;
};

export const isAcceptableFailure = (
	errorMessage: string,
	statusCode: number | undefined,
): boolean => (!statusCode && isUserNetworkFailure(errorMessage)) || statusCode === 401;

// User Network Failure error Messages
export const FAILED_FETCH_ERROR_MESSAGE = 'Failed to fetch';
export const NETWORK_ERROR_MESSAGE = 'NetworkError when attempting to fetch resource.';
export const HOST_NOT_FOUND_ERROR_MESSAGE =
	'A server with the specified hostname could not be found.';

export const userNetworkFailureMessages: string[] = [
	FAILED_FETCH_ERROR_MESSAGE,
	NETWORK_ERROR_MESSAGE,
	HOST_NOT_FOUND_ERROR_MESSAGE,
];

export const isUserNetworkFailure = (errorMessage: string): boolean =>
	userNetworkFailureMessages.some((failureMessage) => errorMessage?.includes(failureMessage));

// Error codes from /invite endpoint
export const EMAIL_BLOCKED = 'EMAIL_BLOCKED';
export const REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS =
	'REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS';
const INVALID_EMAIL = 'INVALID_EMAIL';
const MISSING_USERNAME = 'MISSING_USERNAME';
const EMPTY_USERNAME = 'EMPTY_USERNAME';
const USERNAME_MUST_BE_EMAIL = 'USERNAME_MUST_BE_EMAIL';

export type BadRequesetErrorCodes =
	| typeof EMAIL_BLOCKED
	| typeof INVALID_EMAIL
	| typeof MISSING_USERNAME
	| typeof EMPTY_USERNAME
	| typeof USERNAME_MUST_BE_EMAIL
	| typeof REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS;

// Partial Error type from https://aid-account.stg.internal.atlassian.com/api-explorer#/invite/invite
export type BadRequestErrorResponse = [
	{
		errorCodes: { userName: [BadRequesetErrorCodes] };
		errorMessages: [string];
		errors: { userName: [string] };
		globalErrorCodes: [BadRequesetErrorCodes];
	},
];

// Errors key and message from /invitations/invite which eventually calls /grant-access
export const ERROR_KEY_NO_DEFAULT_GROUP = 'noDefaultGroup';
export const NO_DEFAULT_GROUP_ERROR_MESSAGE = 'Default group for the requested resource not found.';

export interface InviteApiFailureResponse {
	code?: string;
	description?: string;
	key?: string;
	message: string;
	reason?: string;
	status?: number;
}

export const isMessageErrorType = (message: string, error: BadRequesetErrorCodes): boolean => {
	try {
		const badRequestResponse: BadRequestErrorResponse = JSON.parse(message);
		return badRequestResponse.some((val) => val.errorCodes.userName.includes(error));
	} catch (_ignoredError) {
		return false;
	}
};

export const isNoDefaultGroup = (errorKey: string | undefined, errorMessage: string): boolean =>
	errorKey === ERROR_KEY_NO_DEFAULT_GROUP || errorMessage === NO_DEFAULT_GROUP_ERROR_MESSAGE;

export const isAcceptableFailureForUserInvite = (response: InviteApiFailureResponse): boolean => {
	const { key, message, status } = response;
	return (
		isAcceptableFailure(message, status) ||
		(status === 400 &&
			(isMessageErrorType(message, EMAIL_BLOCKED) ||
				isMessageErrorType(message, REACHED_MAX_NUMBER_OF_EMAIL_ALIAS_SIGNUPS))) ||
		(status === 412 && isNoDefaultGroup(key, message))
	);
};

export const BAD_REQUEST = 'bad-request';
export const LICENSE_EXCEEDED = 'licence-exceeded';

export const shouldReportFailedInvite = (response: InviteApiFailureResponse): boolean => {
	const { code, message } = response;
	if (code === BAD_REQUEST && message === 'User cannot invite themselves') {
		return false;
	}

	if (code === LICENSE_EXCEEDED) {
		return false;
	}

	return true;
};

/**
 * Valid values are defined in IIS:
 * https://bitbucket.org/atlassian/id-invitations-service/src/master/src/main/java/com/atlassian/identity/invitations/model/EnablementTouchpoint.java
 */
export const EnablementTouchpoint = {
	CROSSJOIN_NUDGE: 'Crossjoin Nudge',
	DIRECT_ACCESS_JOIN: 'Direct Access - Join',
	DIRECT_ACCESS_AUTOJOIN: 'Direct Access - AutoJoin',
	DIRECT_ACCESS_INVITE_LINKS: 'Direct Access - Invite Links',
	REQUEST_ACCESS: 'Request Access Screen',
	ASSIGNS: 'Assigns',
	CORE_INVITES: 'Core Invites',
	MENTIONS: 'Mentions',
	NAV4_TEAMS_ADD: 'Nav4 Teams Add',
	PEOPLE_DIRECTORY: 'People Directory',
	PROMPT_INVITE_ON_LINK_INSERT: 'Prompt invite on link insert',
	SHARE_DIALOG: 'Share Dialog',
	SPACES: 'Spaces',
	TEAMS_MENU: 'Teams Menu',
	USER_INVITE_DRAWER: 'User Invite Drawer',
	UNKNOWN: 'unknown',
} as const;

export type EnablementTouchpoint = (typeof EnablementTouchpoint)[keyof typeof EnablementTouchpoint];

export type UserRole =
	| 'product/member'
	| 'product/helpseeker'
	| 'product/customer'
	| 'product/external-collaborator-writer'
	| 'product/anonymous'
	| 'product/unlicensed';
